import React, {
    createContext,
    useContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import api from '../services/api'

const WalletContext = createContext(null)

export const WalletProvider = ({ children }) => {
    const [balances, setBalances] = useState({})
    const [pendingBalances, setPendingBalances] = useState({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const normalize = (input, valueKey = 'balance') => {
        const out = {}
        if (!input) return out

        if (Array.isArray(input)) {
            input.forEach((w) => {
                const c = String(w.currency || w.code || '').toUpperCase()
                if (!c) return
                out[c] = Number(w[valueKey] ?? w.balance ?? w.amount ?? 0)
            })
            return out
        }

        if (typeof input === 'object') {
            Object.keys(input).forEach((k) => {
                out[String(k).toUpperCase()] = Number(input[k] || 0)
            })
        }

        return out
    }

    const refreshWallet = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            let res
            try {
                res = await api.get('/dashboard/summary')
            } catch (dashboardError) {
                res = await api.get('/wallet/summary')
            }

            const wallets =
                res.data?.wallets ??
                res.data?.data?.wallets ??
                res.data?.balances ??
                res.data

            setBalances(normalize(wallets, 'available_balance'))
            setPendingBalances(normalize(wallets, 'pending_balance'))
        } catch (e) {
            setError('Failed to load wallet balance')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        refreshWallet()
    }, [refreshWallet])

    const getBalance = useCallback(
        (currency = 'USD') => {
            return Number(balances[String(currency).toUpperCase()] || 0)
        },
        [balances]
    )

    const getPendingBalance = useCallback(
        (currency = 'USD') => {
            return Number(pendingBalances[String(currency).toUpperCase()] || 0)
        },
        [pendingBalances]
    )

    const value = useMemo(
        () => ({
            balances,
            pendingBalances,
            loading,
            error,
            refreshWallet,
            getBalance,
            getPendingBalance,
        }),
        [balances, pendingBalances, loading, error, refreshWallet, getBalance, getPendingBalance]
    )

    return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
}

export const useWallet = () => {
    const ctx = useContext(WalletContext)
    if (!ctx) throw new Error('useWallet must be used inside WalletProvider')
    return ctx
}

export const useWalletSafe = () => useContext(WalletContext)

export default WalletContext
